import React, { CSSProperties, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { Button } from '@/components/atoms'
import { useTranslation } from 'react-i18next'
import { Editor } from '@/components/organisms/settings/editor'
import { MailDeliveryTag } from '@/components/molecules/settings/mail-delivery/mail-delivery-tag'
import { MailTemplateModal } from './mail-template-modal'
import {
  DeliveryMethod,
  MailDeliveryContent,
  MailDeliveryContentInput,
  TemplateContent,
} from '@/models/mail-delivery/mail-delivery-content'
import { v4 as uuid } from 'uuid'
import { ButtonType } from '@/components/atoms/button'
import { Segmented } from '@/components/atoms/segmented'

interface DetailContentFormProps {
  useFormReturn: UseFormReturn<MailDeliveryContentInput>
  detail?: MailDeliveryContent
  hasForeignSMS: boolean
}

export const FormSMSContent: React.FC<DetailContentFormProps> = ({ detail, useFormReturn, hasForeignSMS }) => {
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [contentData, setContentData] = useState({
    id: detail?.id || uuid(),
    content: detail?.content.ja || '',
    contentEn: detail?.content.en || '',
    rawContent: detail?.rawContent.ja || '',
    rawContentEn: detail?.rawContent.en || '',
    isLoadTemplate: false,
  })
  const { t } = useTranslation()
  const { control, register, setValue } = useFormReturn
  const [editorLanguage, setEditorLanguage] = useState<'ja' | 'en'>('ja')

  const watchContent = useWatch({ control, name: 'content' })
  const watchContentEn = useWatch({ control, name: 'contentEn' })

  const onLoadTemplate = (template: TemplateContent) => {
    setContentData({
      id: uuid(),
      content: template.content.ja,
      contentEn: template.content.en,
      rawContent: '',
      rawContentEn: '',
      isLoadTemplate: true,
    })
    setValue('title', template.title.ja)
    setShowTemplateModal(false)
  }

  const inputContentLength = useMemo(() => {
    const selectLangContent = editorLanguage === 'ja' ? watchContent : watchContentEn
    if (!selectLangContent) {
      return 0
    }
    const dummyUrlText = uuid().substr(0, 21)
    const urlReplacedContent = selectLangContent.replaceAll(/%事前チェックインURL%|%ゲストアプリ%/g, dummyUrlText)

    const multiByteLength = urlReplacedContent.replace(/[ -~]/g, '').length
    const halfByteLength = (urlReplacedContent.length - multiByteLength) / 2
    return halfByteLength + multiByteLength
  }, [editorLanguage, watchContent, watchContentEn])

  const editorStyle: CSSProperties = {
    height: '100%',
    width: '100%',
    position: 'absolute',
  }

  return (
    <>
      <div css={inputBlockStyle}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', color: '#272727', fontSize: 14, fontWeight: 'bold' }}>文面</div>
          <Button
            buttonType={ButtonType.Secondary}
            fontSize={11}
            borderRadius={16}
            width={143}
            height={27}
            marginLeft={22}
            onClick={() => setShowTemplateModal(true)}
          >
            {t('Select a template')}
          </Button>
        </div>
        <label style={{ paddingTop: 16 }}>
          {t('mailDelivery.title')}
          <span style={{ fontSize: 10, paddingLeft: 5 }}>※</span>
        </label>
        <input {...register('title')} style={{ width: 715 }} placeholder={t('Enter subject')} />
        <div className="editor-content input-space">
          <label>
            {t('mailDelivery.body')}
            <span style={{ fontSize: 10, paddingLeft: 5 }}>※</span>
          </label>
          {hasForeignSMS && (
            <Segmented<'ja' | 'en'>
              value={editorLanguage}
              options={[
                { value: 'ja', label: '日本語' },
                { value: 'en', label: '英語' },
              ]}
              onChange={v => setEditorLanguage(v)}
            />
          )}
          <div css={editorBlockStyle}>
            <div style={{ flex: '0 0 715px' }}>
              <div style={{ position: 'relative', height: 294 }}>
                <Editor
                  id={contentData?.id}
                  placeholder={t('Enter text')}
                  className="content-editor"
                  isShowVideoIcon={false}
                  isHideToolbar={true}
                  htmlContent={contentData?.content}
                  rawContent={contentData?.rawContent}
                  onChange={(raw: string, html: string, text: string) => {
                    setValue('content', text, { shouldDirty: true })
                    setValue('rawContent', raw, { shouldDirty: true })
                  }}
                  style={{ ...editorStyle, visibility: editorLanguage === 'ja' ? 'visible' : 'hidden' }}
                  doFocus={contentData?.isLoadTemplate || false}
                />
                <Editor
                  id={contentData?.id}
                  placeholder={t('Enter text')}
                  className="content-editor"
                  isShowVideoIcon={false}
                  isHideToolbar={true}
                  htmlContent={contentData?.contentEn}
                  rawContent={contentData?.rawContentEn}
                  onChange={(raw: string, html: string, text: string) => {
                    setValue('contentEn', text, { shouldDirty: true })
                    setValue('rawContentEn', raw, { shouldDirty: true })
                  }}
                  style={{ ...editorStyle, visibility: editorLanguage === 'en' ? 'visible' : 'hidden' }}
                  doFocus={contentData?.isLoadTemplate || false}
                />
              </div>
              <div className="editor-footer">
                <div className="editor-footer__count">
                  <span>{inputContentLength.toFixed(1)}</span>/670文字
                </div>
                <div className="editor-footer__note">
                  <p>※タグ名でカウントを行いますが、送信時に予約情報が挿入されるため、文字数には余裕を持たせてください。</p>
                  <p>
                    ※全角70文字未満が推奨です。70文字以上になると、67文字ごとに1通としてカウントされます。半角英数字は0.5文字、URLは10.5文字でカウントされます。
                  </p>
                </div>
              </div>
            </div>
            <MailDeliveryTag />
          </div>
        </div>
      </div>
      {showTemplateModal && (
        <MailTemplateModal method={DeliveryMethod.SMS} onSave={onLoadTemplate} onClose={() => setShowTemplateModal(false)} />
      )}
    </>
  )
}

const inputBlockStyle = css({
  padding: '25px 32px 0 32px',
  label: {
    display: 'block',
    marginBottom: 12,
    fontSize: 12,
    fontWeight: 'bold',
  },
  input: {
    height: 32,
    padding: '8px 16px',
    border: '1px solid #CCCCCC',
    borderRadius: 17,
    textAlign: 'left',
    '::placeholder': {
      color: '#CCCCCC',
    },
  },
  '.input-space': { marginTop: 24 },
})
const editorBlockStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  gap: 16,
  '.content-editor': {
    '> div:last-of-type': { borderRadius: '12px 12px 0 0' },
  },
  '.editor-footer': {
    padding: '16px 24px',
    backgroundColor: '#F2F2F2',
    border: '1px solid #CCCCCC',
    borderTop: 'none',
    borderRadius: '0 0 12px 12px',
    display: 'flex',
  },
  '.editor-footer__count': {
    fontSize: 12,
    color: '#676767',
    fontWeight: 'bold',
    letterSpacing: 1.2,
    minWidth: 120,
    span: { fontSize: 14, letterSpacing: 1.4 },
  },
  '.editor-footer__note': {
    fontSize: 12,
    color: '#676767',
    letterSpacing: 1.2,
    lineHeight: 1.5,
    p: { textIndent: '-1em', marginLeft: '1em' },
  },
})
